import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import CardImage from '../components/CardImage';
import { useCardImage } from '../api/cardCache';
import { cardImageUrl } from '../config';
import { banlistGroups, BAN_BADGE, BANLIST_NAME, type BanStatus } from '../banlist';

function BanlistCard({ name }: { name: string }) {
  const img = useCardImage(name);
  return (
    <Link to={`/card/${encodeURIComponent(name)}`} className="banlist-card" title={name}>
      <CardImage src={cardImageUrl(img)} alt={name} />
      <span className="banlist-card-name">{name}</span>
    </Link>
  );
}

const FORMAT_RULES = [
  'Each player begins the Duel with 4000 LP.',
  'Each player begins with 4 cards in the starting hand.',
  'Main Deck: 20–30 cards.',
  'Extra Deck: up to 6 cards.',
  'Side Deck: up to 6 cards.',
  'Each player can use up to 1 Skill Card.',
  'Each player has 3 Main Monster Zones, 3 Spell & Trap Zones, 1 Field Zone, and a Graveyard — there are no Extra Monster Zones.',
  'There is no Main Phase 2.',
  'A maximum of 3 copies of any single card, counted across the Main, Extra, and Side Decks combined.',
];

const LIST_INFO: Record<BanStatus, { title: string; desc: string }> = {
  forbidden: {
    title: 'Forbidden',
    desc: 'These cards cannot be used in the Main Deck, Extra Deck, Side Deck, or as your Skill Card.',
  },
  limit1: {
    title: 'Limit 1',
    desc: 'A maximum of one card total from this entire list may be included in the Main Deck, Extra Deck, Side Deck, and Skill Card combined.',
  },
  limit2: {
    title: 'Limit 2',
    desc: 'A maximum of two cards total from this entire list — any combination, which may be two copies of the same card or one copy each of two different cards.',
  },
  limit3: {
    title: 'Limit 3',
    desc: 'A maximum of three cards total from this entire list — any combination of cards, up to three from the list.',
  },
};
const ORDER: BanStatus[] = ['forbidden', 'limit1', 'limit2', 'limit3'];

export default function RulingsPage() {
  const groups = banlistGroups();
  return (
    <div>
      <SiteHeader />
      <main className="page page--wide">
        <h1>Speed Duel Rules &amp; Banlist</h1>
        <p className="muted">How the Speed Duel format works, and the current Forbidden &amp; Limited list.</p>

        <h2>Format Rules</h2>
        <p>Speed Duel follows the standard Yu-Gi-Oh! TCG rules, with the following differences:</p>
        <ul className="rules-list">
          {FORMAT_RULES.map((r, i) => <li key={i}>{r}</li>)}
        </ul>

        <h2>Card Legality</h2>
        <p>
          Speed Duel cards carry the Speed Duel logo (a watermark in the text box, plus a “Speed Duel” tag on
          non-Skill cards). A card must have that symbol to be used in the format. Skill Cards are unique to
          Speed Duel and can’t be used in other formats. Official tournaments also follow the Forbidden &amp;
          Limited list below.
        </p>

        <h2>Forbidden &amp; Limited List <span className="muted">· {BANLIST_NAME}</span></h2>
        {ORDER.map(st => (
          <section key={st} className="banlist-section">
            <h3>
              <span className={`ban-badge ban-inline ${BAN_BADGE[st].cls}`}>{BAN_BADGE[st].symbol}</span>
              {' '}{LIST_INFO[st].title} <span className="muted">· {groups[st].length}</span>
            </h3>
            <p className="muted banlist-desc">{LIST_INFO[st].desc}</p>
            <div className="banlist-cards">
              {groups[st].map(name => <BanlistCard key={name} name={name} />)}
            </div>
          </section>
        ))}

        <p className="muted" style={{ marginTop: 28 }}>
          Sources:{' '}
          <a href="https://yugipedia.com/wiki/Speed_Duel" target="_blank" rel="noreferrer">Speed Duel (Yugipedia)</a>
          {' · '}
          <a href="https://yugipedia.com/wiki/July_2026_Lists_(Speed_Duel)" target="_blank" rel="noreferrer">July 2026 Lists</a>
        </p>
      </main>
    </div>
  );
}
